import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightning, X, Clock, Users, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WigglezMark } from "@/components/WigglezMark";
import { fbTrack } from "@/lib/fbpixel";

export default function PreOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drops, setDrops] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", size: "" });
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, new Date("2026-08-18T00:00:00").getTime() - now);
  const cd = {
    d: Math.floor(diff / 86400000), h: Math.floor(diff / 3600000) % 24,
    m: Math.floor(diff / 60000) % 60, s: Math.floor(diff / 1000) % 60,
  };

  const load = () => api.get("/preorders/drops").then(({ data }) => setDrops(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openReserve = (d) => { setForm({ name: "", email: "", size: (d.sizes || [])[0] || "" }); setActive(d); };

  const submit = async () => {
    if (!form.email) { toast.error("Add your email so we can hold your spot"); return; }
    setBusy(true);
    try {
      await api.post("/preorders", { drop_id: active.id, name: form.name, email: form.email, size: form.size });
      fbTrack("Lead", { content_name: "preorder-rsvp" });
      toast.success(`Reserved — you're on the list for ${active.name} 🐾`);
      setActive(null); load();
    } catch { toast.error("Couldn't reserve — try again"); }
    finally { setBusy(false); }
  };

  const payDeposit = async () => {
    if (!user) { toast.error("Sign in to place a refundable deposit"); navigate("/auth"); return; }
    if (!form.email) { toast.error("Add your email so we can hold your spot"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/preorders/deposit-checkout", {
        drop_id: active.id, name: form.name, email: form.email, size: form.size, origin_url: window.location.origin,
      });
      fbTrack("InitiateCheckout", { value: active.deposit, currency: "USD", content_name: "preorder-deposit" });
      window.location.href = data.checkout_url;
    } catch { toast.error("Couldn't start your deposit — try again"); setBusy(false); }
  };

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="preorders-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute -right-24 top-0 h-[26rem] w-[26rem] rounded-full soul-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,20,147,0.4) 0%, rgba(255,20,147,0) 70%)", filter: "blur(14px)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center">
          <p className="overline text-primary mb-4 inline-flex items-center gap-2"><Lightning weight="fill" size={14} /> Pre-Order · Reserve the next drop</p>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl max-w-4xl mx-auto">
            Lock it in <span className="neon-text">before</span> it drops.
          </h1>
          <p className="text-muted-foreground mt-5 max-w-2xl mx-auto">These pieces aren't out yet. Reserve your size now — no charge today, you'll be first in line and first to pay when the drop goes live. Limited runs sell out.</p>

          <div className="mt-9 flex items-center justify-center gap-4 sm:gap-6" data-testid="resurrection-countdown">
            {[["DAYS", cd.d], ["HRS", cd.h], ["MIN", cd.m], ["SEC", cd.s]].map(([l, v]) => (
              <div key={l} className="text-center">
                <div className="font-display font-black text-4xl sm:text-6xl neon-text tabular-nums leading-none">{String(v).padStart(2, "0")}</div>
                <div className="overline text-muted-foreground text-[10px] mt-1">{l}</div>
              </div>
            ))}
          </div>
          <p className="overline mt-4" style={{ color: "#ccff00" }}>The Resurrection · Aug 18 · Born again</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="preorder-grid">
          {drops.map((d, i) => (
            <motion.div key={d.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.07 }}
              className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] hover:border-primary/50 transition-colors" data-testid={`preorder-card-${d.id}`}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={d.image} alt={d.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" style={{ filter: "saturate(1.05)" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/20" />
                <span className="absolute top-3 left-3 overline px-3 py-1 rounded-full bg-primary/90 text-primary-foreground inline-flex items-center gap-1"><Clock weight="fill" size={11} /> {d.drop_label}</span>
                <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/55 backdrop-blur-sm border border-white/10"><WigglezMark size="text-[11px]" /></div>
                <div className="absolute bottom-3 left-4 right-4">
                  <h3 className="font-display font-black uppercase text-2xl leading-tight">{d.name}</h3>
                  <p className="overline text-secondary mt-1 inline-flex items-center gap-1.5"><Users weight="fill" size={12} /> {d.reserved} already reserved</p>
                </div>
              </div>
              <div className="p-5 flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xl font-modern">{d.price > 0 ? `$${d.price.toLocaleString()}` : "Free RSVP"}</span>
                  {d.deposit > 0 && <span className="text-[11px] text-secondary inline-flex items-center gap-1 mt-0.5"><ShieldCheck weight="fill" size={11} /> ${d.deposit} refundable deposit</span>}
                </div>
                <button onClick={() => openReserve(d)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-[10px] font-modern hover:bg-primary/90 transition-colors" data-testid={`preorder-reserve-${d.id}`}>
                  <Lightning weight="fill" size={15} /> Reserve yours
                </button>
              </div>
            </motion.div>
          ))}
        </div>
        {drops.length === 0 && <p className="text-center text-muted-foreground py-16">No drops open for pre-order right now — check back soon. 🐾</p>}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur grid place-items-center p-4" onClick={() => setActive(null)} data-testid="preorder-modal">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              className="w-full max-w-md rounded-2xl border border-primary/30 bg-[#0b0b0b] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="overline text-primary inline-flex items-center gap-2"><Lightning weight="fill" size={14} /> {active.drop_label}</p>
                  <h3 className="font-display font-black uppercase text-2xl mt-1">Reserve · {active.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">No charge today. We'll email you the second it drops.</p>
                </div>
                <button onClick={() => setActive(null)} className="text-muted-foreground hover:text-white" data-testid="preorder-close"><X size={22} /></button>
              </div>
              <div className="space-y-4">
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name"
                  className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary transition-colors text-sm" data-testid="preorder-name" />
                <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email (to hold your spot)" type="email"
                  className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary transition-colors text-sm" data-testid="preorder-email" />
                {(active.sizes || []).length > 0 && (
                  <div>
                    <p className="overline text-muted-foreground mb-2">Size</p>
                    <div className="flex flex-wrap gap-2">
                      {active.sizes.map((s) => (
                        <button key={s} onClick={() => setForm((f) => ({ ...f, size: s }))}
                          className={`px-4 py-2 rounded-[10px] border text-sm transition-colors ${form.size === s ? "border-primary bg-primary/10 text-white" : "border-white/15 text-muted-foreground hover:border-white/30"}`}
                          data-testid={`preorder-size-${s}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={submit} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-[10px] font-modern hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="preorder-submit">
                  <CheckCircle weight="fill" size={18} /> {busy ? "Reserving…" : "Reserve my spot (free)"}
                </button>
                {active.deposit > 0 && (
                  <>
                    <div className="flex items-center gap-3 my-1">
                      <div className="h-px flex-1 bg-white/10" /><span className="overline text-muted-foreground text-[10px]">or lock it in</span><div className="h-px flex-1 bg-white/10" />
                    </div>
                    <button onClick={payDeposit} disabled={busy}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] font-modern text-black transition-transform hover:scale-[1.02] disabled:opacity-50"
                      style={{ background: "#ccff00" }} data-testid="preorder-deposit">
                      <ShieldCheck weight="fill" size={18} /> {busy ? "Opening checkout…" : `Lock it in — $${active.deposit} deposit`}
                    </button>
                    <p className="text-[11px] text-muted-foreground text-center leading-snug">
                      100% refundable anytime before the drop — no risk. Your deposit is credited toward the full price on launch day, and locks your size & spot in line.
                    </p>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
